/**
 * Uzbek Latin (1995 alphabet) → Uzbek Cyrillic, for GENERATING the uz-Cyrl UI
 * locale from the hand-written Latin `uz` files (scripts/gen-uz-cyrl.mjs).
 *
 * Heuristic by nature — the Latin alphabet lost distinctions Cyrillic keeps
 * (е/э, ц/тс, the ъ sign) — so the generator applies an overrides file on
 * top and prints the ambiguous spots for review. Everything that is not
 * Uzbek prose passes through untouched: i18next interpolations `{{x}}`,
 * nesting `$t(...)`, HTML tags, URLs and e-mails, ALL-CAPS tokens (product
 * names, acronyms such as PDF, E-IMZO — vowel-less caps or the protected
 * list; caps WITH vowels such as SHABLON are prose) and a protected-word list.
 *
 * Rules (applied per word, longest match first):
 *   oʻ/o'/o‘/o’ → ў    gʻ/g'/g‘/g’ → ғ    sh → ш    ch → ч    ts → ц
 *   yo → ё   yu → ю   ya → я   ye → е (word-initial or after a vowel; else й+е)
 *   e → э at the start of a word, е elsewhere      x → х   q → қ   h → ҳ
 *   j → ж   y → й   the tutuq belgisi (ʼ ’ ') after a consonant → ъ
 *   a capitalised digraph (Sh, Ch, Yo…) yields one capital letter.
 *
 * Two rules added 2026-09-19 (the abat/docs/office copies carry the same code):
 *   `opts.words` — an exact-token map for abbreviations that DO take a
 *   Cyrillic form (SN → СН, ERI → ЭРИ, STIR → СТИР); the acronym rule
 *   above would keep them Latin.
 *   a protected/mapped/acronym HEAD followed by a suffix — `E-IMZO’da`,
 *   `API-kalit`, `docs’ga`, `DAATdan`, `STIRi` — keeps (or maps) the head
 *   and transliterates only the suffix: `E-IMZO’да`, `API-калит`,
 *   `docs’га`, `ДААТдан`, `СТИРи`. Before this, the whole token failed the
 *   protected check and came out as `Э-ИМЗЎда`.
 */

const APOSTROPHES = /[ʻʼ‘’'`´]/g;

const SIMPLE = {
  a: 'а', b: 'б', d: 'д', f: 'ф', g: 'г', h: 'ҳ', i: 'и', j: 'ж', k: 'к', l: 'л', m: 'м',
  n: 'н', o: 'о', p: 'п', q: 'қ', r: 'р', s: 'с', t: 'т', u: 'у', v: 'в', x: 'х', y: 'й', z: 'з',
};
const VOWELS = new Set('aeiouoʻ'.split(''));
const DIGRAPHS = { sh: 'ш', ch: 'ч', yo: 'ё', yu: 'ю', ya: 'я', ts: 'ц' };

const PROTECTED_DEFAULT = ['Zehn', 'Zehnmind', 'Collabora', 'E-IMZO', 'EIMZO', 'PDF', 'DOCX', 'PKCS', 'QR', 'ID', 'MinIO', 'ISSAI', 'INTOSAI', 'IDI', 'PINFL', 'API', 'URL', 'HTML', 'JSON', 'MCP', 'LLM', 'OCR', 'WOPI', 'Word', 'Excel', 'PowerPoint', 'UTC', 'ISO', 'CSV', 'PNG', 'JPG', 'JPEG', 'ZIP', 'TXT', 'RTF', 'ODT', 'XLS', 'XLSX', 'PPT', 'PPTX', 'DOC', 'TSA', 'CMS', 'SHA', 'RSA', 'IP', 'TLS', 'SSL', 'HTTP', 'HTTPS', 'SMS', 'TIN', 'STIR', 'INN', 'PIN', 'ERI', 'AI', 'MB', 'KB', 'GB', 'ABAT', 'AMIS', 'SAD', 'QC', 'MCP', 'NATS', 'VPN', 'DNS', 'JWT', 'CI', 'CD', 'IT', 'UI', 'UX'];

function cased(latin, cyr) {
  // `latin` is the source slice; its first letter's case decides the output.
  const first = latin[0];
  if (first !== first.toLowerCase() && latin.slice(1).toLowerCase() === latin.slice(1)) {
    return cyr[0].toUpperCase() + cyr.slice(1);
  }
  if (latin.toUpperCase() === latin && latin.length > 1) return cyr.toUpperCase();
  return cyr;
}

/** One word of Uzbek letters (apostrophes already normalised to ʼ). */
function translitWord(word, report, context) {
  let out = '';
  let i = 0;
  const lower = word.toLowerCase();
  while (i < word.length) {
    const ch = word[i];
    const lo = lower[i];
    const next = lower[i + 1] || '';
    const prev = lower[i - 1] || '';
    const atStart = i === 0;

    if (ch === 'ʼ') {
      // After o/g it is the Latin letter oʻ/gʻ (handled below); after any
      // other letter it is the tutuq belgisi → ъ (maʼlumot → маълумот,
      // masʼul → масъул); a stray one is dropped.
      if (prev && /[a-z]/.test(prev)) out += 'ъ';
      i += 1;
      continue;
    }
    if ((lo === 'o' || lo === 'g') && next === 'ʼ') {
      out += cased(ch, lo === 'o' ? 'ў' : 'ғ');
      i += 2;
      continue;
    }
    const pair = lo + next;
    if (pair === 'ye') {
      const afterVowel = VOWELS.has(prev) || prev === 'ʼ';
      if (atStart || afterVowel) {
        out += cased(word.slice(i, i + 2), 'е');
      } else {
        out += cased(ch, 'й') + 'е';
      }
      i += 2;
      continue;
    }
    // `yoʻ` is y + the letter oʻ (yoʻq → йўқ), not the digraph yo.
    if (DIGRAPHS[pair] && !(pair === 'yo' && (lower[i + 2] || '') === 'ʼ')) {
      if (pair === 'ts' && report) report.push({ kind: 'ts', context });
      out += cased(word.slice(i, i + 2), DIGRAPHS[pair]);
      i += 2;
      continue;
    }
    if (lo === 'e') {
      if (atStart) {
        if (report) report.push({ kind: 'initial-e', context });
        out += cased(ch, 'э');
      } else {
        out += cased(ch, 'е');
      }
      i += 1;
      continue;
    }
    if (lo === 'c') {
      // Not an Uzbek letter on its own (only in `ch`); foreign words keep it.
      out += ch;
      i += 1;
      continue;
    }
    const cyr = SIMPLE[lo];
    out += cyr ? cased(ch, cyr) : ch;
    i += 1;
  }
  return out;
}

// A token starts with a letter and may carry hyphens (E-IMZO, ko‘p-chilik),
// so a hyphenated acronym is judged as one word; one with underscores
// (VITE_EIMZO_API_KEYS) is an identifier, never prose, and passes through.
const WORD = /[A-Za-zʼ][A-Za-zʼ_-]*/g;

const isAcronym = (bare) =>
  bare.length > 1 && bare === bare.toUpperCase() && /[A-Z]/.test(bare) && !/[AEIOU]/.test(bare);

/** How a whole token (no apostrophes) is kept or mapped; null = transliterate it. */
function keepOrMap(bare, protectedWords, words) {
  if (!bare) return null;
  if (Object.prototype.hasOwnProperty.call(words, bare)) return words[bare];
  if (protectedWords.has(bare) || protectedWords.has(bare.replace(/-/g, ''))) return bare;
  // An ALL-CAPS token without a vowel is an acronym (PDF, PKCS); one with
  // vowels is shouted prose (SHABLON, MENYU) and transliterates in caps.
  if (isAcronym(bare)) return bare;
  return null;
}

/**
 * A kept/mapped head with an Uzbek suffix: `E-IMZOʼda`, `API-kalit`,
 * `DAATdan`. Candidates are the prefixes ending at an apostrophe, a hyphen
 * or a caps→lowercase boundary, longest first; a one-letter head never
 * qualifies (`Oʻzbekcha`, `gʻoya` are letters, not heads). Returns
 * [head, separator, rest] or null.
 */
function splitHead(word, protectedWords, words) {
  const cuts = [];
  for (let i = 1; i < word.length; i += 1) {
    const ch = word[i];
    if (ch === 'ʼ' || ch === '-') cuts.push({ at: i, sep: ch === 'ʼ' ? '’' : '-', skip: 1 });
    else if (/[a-z]/.test(ch) && /[A-Z]/.test(word[i - 1]) && /^[A-Z-]+$/.test(word.slice(0, i))) cuts.push({ at: i, sep: '', skip: 0 });
  }
  for (const cut of cuts.reverse()) {
    const head = word.slice(0, cut.at);
    const rest = word.slice(cut.at + cut.skip);
    if (head.length < 2 || !rest) continue;
    const kept = keepOrMap(head.replace(/ʼ/g, ''), protectedWords, words);
    if (kept !== null) return [kept, cut.sep, rest];
  }
  return null;
}

/**
 * @param {string} text - one Latin Uzbek string
 * @param {{ protectedWords?: string[], words?: Record<string, string>, report?: Array }} [opts]
 * @returns {string}
 */
export function latinToCyrillic(text, opts = {}) {
  if (typeof text !== 'string' || !text) return text;
  const report = opts.report || null;
  const protectedWords = new Set([...PROTECTED_DEFAULT, ...(opts.protectedWords || [])]);
  const words = opts.words || {};

  // Carve out what must never be transliterated; each hole is restored after.
  const holes = [];
  const hole = (match) => {
    holes.push(match);
    return `\uE000${holes.length - 1}\uE000`;
  };
  let s = text
    .replace(/\{\{[^}]*\}\}/g, hole)
    .replace(/\$t\([^)]*\)/g, hole)
    .replace(/<[^>]+>/g, hole)
    .replace(/https?:\/\/\S+|[\w.+-]+@[\w-]+\.[\w.-]+/g, hole)
    .replace(/[&][a-z]+;/gi, hole);

  s = s.replace(APOSTROPHES, 'ʼ');

  s = s.replace(WORD, (word) => {
    const bare = word.replace(/ʼ/g, '');
    if (!bare) return '';
    if (bare.includes('_')) return word;
    const whole = keepOrMap(bare, protectedWords, words);
    if (whole !== null) return whole === bare ? word : whole;
    const split = splitHead(word, protectedWords, words);
    if (split) {
      const [head, sep, rest] = split;
      return head + sep + translitWord(rest, report, text);
    }
    return translitWord(word, report, text);
  });

  // Private-use sentinels: never in a UI string, and not a control character.
  return s.replace(/\uE000(\d+)\uE000/g, (_, n) => holes[Number(n)]);
}

/** Walk a locale object (nested plain objects of strings). */
export function transliterateTree(node, opts = {}) {
  if (typeof node === 'string') return latinToCyrillic(node, opts);
  if (Array.isArray(node)) return node.map((v) => transliterateTree(v, opts));
  if (node && typeof node === 'object') {
    const out = {};
    for (const [key, value] of Object.entries(node)) out[key] = transliterateTree(value, opts);
    return out;
  }
  return node;
}
